<template>
  <div class="flex flex-col" style="height: calc(100vh - 165px);">
    <!-- Stats Row -->
    <div class="px-4 py-2.5 border-b border-gray-200 bg-white shrink-0">
      <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">My Calendar</div>
      <!-- Stats skeleton -->
      <div v-if="loading" class="flex items-center gap-2">
        <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
          <div class="h-5 w-20 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
          <div class="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
          <div class="h-5 w-20 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
      <!-- Stats loaded -->
      <div v-else class="flex items-center gap-2">
        <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
          <span class="text-base font-bold text-gray-900">{{ stats.totalMonth }}</span>
          <span class="text-[10px] uppercase tracking-wider text-gray-400">This Month</span>
        </div>
        <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-50 border border-blue-200">
          <span class="text-base font-bold text-blue-700">{{ stats.dueToday }}</span>
          <span class="text-[10px] uppercase tracking-wider text-blue-600">Due Today</span>
        </div>
        <div
          class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border"
          :class="stats.overdue > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'"
        >
          <span class="text-base font-bold" :class="stats.overdue > 0 ? 'text-red-700' : 'text-gray-900'">{{ stats.overdue }}</span>
          <span class="text-[10px] uppercase tracking-wider" :class="stats.overdue > 0 ? 'text-red-600' : 'text-gray-400'">Overdue</span>
        </div>
      </div>
    </div>

    <!-- Main content: calendar+tasks (left) + drawer (right) -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Calendar + Task List -->
      <div
        :style="{ width: selectedPatient ? splitPct + '%' : '100%' }"
        class="flex flex-col overflow-y-auto overflow-x-hidden min-w-0"
      >
        <!-- Month Navigation -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shrink-0">
          <div class="flex items-center gap-3">
            <button class="cm-icon-btn" @click="prevMonth">
              <font-awesome-icon :icon="['fas', 'chevron-left']" class="text-xs" />
            </button>
            <h3 class="text-sm font-semibold text-gray-800 w-36 text-center">{{ monthLabel }}</h3>
            <button class="cm-icon-btn" @click="nextMonth">
              <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-xs" />
            </button>
          </div>
          <button class="text-xs font-medium text-primary-600 hover:text-primary-700 px-2 py-1 rounded hover:bg-primary-50" @click="goToday">
            Today
          </button>
        </div>

        <!-- Calendar Grid -->
        <div class="p-4">
          <!-- Day-of-week headers -->
          <div class="grid grid-cols-7 mb-1">
            <div
              v-for="day in weekDays"
              :key="day"
              class="text-center text-[10px] uppercase tracking-wider text-gray-400 font-semibold py-1"
            >{{ day }}</div>
          </div>

          <!-- Calendar skeleton -->
          <div v-if="loading" class="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
            <div
              v-for="n in 35"
              :key="'skel-' + n"
              class="min-h-[72px] p-2 bg-white"
            >
              <div class="h-3.5 w-5 bg-gray-200 rounded animate-pulse"></div>
              <div v-if="n % 7 < 5 && n > 7" class="mt-2 h-4 w-10 bg-gray-100 rounded animate-pulse"></div>
            </div>
          </div>

          <!-- Day cells -->
          <div v-else class="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
            <button
              v-for="(day, i) in calendarDays"
              :key="i"
              class="min-h-[72px] p-2 text-left transition-colors relative"
              :class="dayCellClasses(day)"
              :style="dayCellStyle(day)"
              @click="selectDate(day.date)"
            >
              <span class="text-xs font-medium" :class="dayNumberClasses(day)">{{ day.dayNumber }}</span>
              <div v-if="day.taskCount > 0" class="mt-1.5 flex items-center gap-1">
                <span
                  class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold"
                  :class="day.hasOverdue
                    ? 'bg-red-500 text-white'
                    : 'bg-[#F76734] text-white'"
                >{{ day.taskCount }}</span>
                <span
                  v-if="day.taskCount === 1"
                  class="text-[10px] text-gray-500 truncate"
                >task</span>
                <span
                  v-else
                  class="text-[10px] text-gray-500 truncate"
                >tasks</span>
              </div>
            </button>
          </div>
        </div>

        <!-- Selected Day Task List -->
        <div v-if="selectedDate && !loading" class="px-4 pb-4">
          <div class="border border-gray-200 rounded-lg overflow-hidden">
            <div class="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h4 class="text-xs font-semibold text-gray-700">{{ selectedDateLabel }}</h4>
              <span class="text-[10px] text-gray-400">{{ selectedDateTasks.length }} task{{ selectedDateTasks.length !== 1 ? 's' : '' }}</span>
            </div>
            <div v-if="selectedDateTasks.length === 0" class="px-4 py-6 text-center text-xs text-gray-400">
              No tasks due on this day.
            </div>
            <div v-else>
              <button
                v-for="(item, idx) in selectedDateTasks"
                :key="idx"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-b border-gray-100 last:border-b-0"
                :class="selectedPatient && selectedPatient.entity_id === item.entityId ? 'bg-primary-50' : 'hover:bg-gray-50'"
                @click="openPatient(item)"
              >
                <div class="flex-1 min-w-0">
                  <div class="text-xs font-medium text-gray-900 truncate">
                    {{ item.patientName }}
                    <span v-if="item.patientDob" class="font-normal text-gray-400 ml-1">DOB: {{ formatDob(item.patientDob) }}</span>
                  </div>
                  <div class="text-[10px] text-gray-500 truncate">{{ item.metricName }} · {{ item.taskType }}</div>
                </div>
                <div class="shrink-0 flex items-center gap-2">
                  <span
                    v-if="item.isOverdue"
                    class="text-[10px] font-semibold text-red-600 uppercase"
                  >Overdue</span>
                  <span class="text-[10px] text-gray-400">{{ item.dueDate }}</span>
                  <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-[10px] text-gray-300" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Drag handle -->
      <div
        v-if="selectedPatient"
        class="w-1 cursor-col-resize bg-gray-200 hover:bg-primary-400 active:bg-primary-500 transition-colors shrink-0"
        @mousedown="startDrag"
      ></div>

      <!-- Patient Drawer -->
      <div
        v-if="selectedPatient"
        :style="{ width: (100 - splitPct) + '%' }"
        class="flex flex-col overflow-hidden"
      >
        <div class="flex-1 overflow-hidden">
          <Patient_Drawer
            ref="patientDrawer"
            :patient-identifier="selectedPatient.patient_identifier"
            :entity-id="selectedPatient.entity_id"
            :patient-data="selectedPatient"
            :patient-name="selectedPatient.patient_name"
            :enabled-tabs="drawerEnabledTabs"
            :show-header="true"
            :default-tab="'programs'"
            metric-category="Care Management"
            @close="closeDrawer"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import { fetchPatientWorkQueue } from '@/services/api/patientWorkQueue';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  format,
  isSameDay,
  isToday,
  isBefore,
  isSameMonth,
} from 'date-fns';

export default {
  name: 'CareManagement_MyCalendar',
  components: { FontAwesomeIcon, Patient_Drawer },
  data() {
    return {
      currentMonth: new Date(),
      selectedDate: new Date(),
      allPatients: [],
      myEntityId: null,
      loading: false,
      weekDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      selectedPatient: null,
      splitPct: parseInt(localStorage.getItem('cm_cal_split_pct') || '55'),
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug']),
    drawerEnabledTabs() {
      const base = ['details', 'utilization', 'programs', 'engagement', 'care_plan', 'care_team'];
      return this.current_tenant_slug === 'ihci' ? [...base, 'insights'] : base;
    },
    monthLabel() {
      return format(this.currentMonth, 'MMMM yyyy');
    },
    calendarDays() {
      const monthStart = startOfMonth(this.currentMonth);
      const monthEnd = endOfMonth(this.currentMonth);
      const gridStart = startOfWeek(monthStart);
      const gridEnd = endOfWeek(monthEnd);

      const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
      const today = new Date();

      return days.map((date) => {
        const key = format(date, 'yyyy-MM-dd');
        const tasks = this.tasksByDate[key] || [];
        return {
          date,
          dayNumber: date.getDate(),
          isCurrentMonth: isSameMonth(date, this.currentMonth),
          isToday: isToday(date),
          isSelected: this.selectedDate && isSameDay(date, this.selectedDate),
          taskCount: tasks.length,
          hasOverdue: tasks.some((t) => t.isOverdue),
          isPast: isBefore(date, today) && !isToday(date),
        };
      });
    },
    tasksByDate() {
      const map = {};
      for (const patient of this.allPatients) {
        for (const task of patient.tasks || []) {
          if (!task.due_date) continue;
          const key = task.due_date;
          if (!map[key]) map[key] = [];
          map[key].push({
            patientName: patient.patient_name,
            patientIdentifier: patient.patient_identifier,
            patientDob: patient.patient_dob || patient.bene_dob || '',
            entityId: patient.entity_id,
            metricName: task.metric_name || '',
            taskType: task.task_type || '',
            dueDate: task.due_date,
            isOverdue: task.is_overdue,
            taskId: task.id,
          });
        }
      }
      return map;
    },
    selectedDateTasks() {
      if (!this.selectedDate) return [];
      const key = format(this.selectedDate, 'yyyy-MM-dd');
      return this.tasksByDate[key] || [];
    },
    selectedDateLabel() {
      if (!this.selectedDate) return '';
      return format(this.selectedDate, 'EEEE, MMMM d, yyyy');
    },
    maxTaskCount() {
      let max = 0;
      for (const day of this.calendarDays) {
        if (day.isCurrentMonth && day.taskCount > max) max = day.taskCount;
      }
      return max;
    },
    stats() {
      const today = new Date().toISOString().slice(0, 10);
      const monthStart = format(startOfMonth(this.currentMonth), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(this.currentMonth), 'yyyy-MM-dd');

      let totalMonth = 0;
      let dueToday = 0;
      let overdue = 0;

      for (const [dateKey, tasks] of Object.entries(this.tasksByDate)) {
        if (dateKey >= monthStart && dateKey <= monthEnd) {
          totalMonth += tasks.length;
        }
        if (dateKey === today) {
          dueToday += tasks.length;
        }
        for (const task of tasks) {
          if (task.isOverdue) overdue++;
        }
      }

      return { totalMonth, dueToday, overdue };
    },
  },
  async mounted() {
    await this.loadMyEntity();
    await this.loadPatients();
  },
  methods: {
    async loadMyEntity() {
      try {
        const { data } = await this.$axios.get('/api/entities/appuser-entity/');
        const entities = data.user_entities || data;
        if (entities.length) {
          this.myEntityId = entities[0].id;
        }
      } catch {
        // User may not have a CM entity
      }
    },
    async loadPatients() {
      if (!this.myEntityId) return;
      this.loading = true;
      try {
        const data = await fetchPatientWorkQueue({
          start_row: 0,
          end_row: 999999,
          task_owner_id: this.myEntityId,
          filter_args: {
            metric_ids: [],
            show_completed: false,
            include_snoozed: false,
          },
        });
        if (data?.success) {
          this.allPatients = data.rowData || [];
        }
      } catch (error) {
        console.error('Error loading patients:', error);
      } finally {
        this.loading = false;
      }
    },
    prevMonth() {
      this.currentMonth = subMonths(this.currentMonth, 1);
    },
    nextMonth() {
      this.currentMonth = addMonths(this.currentMonth, 1);
    },
    goToday() {
      this.currentMonth = new Date();
      this.selectedDate = new Date();
    },
    selectDate(date) {
      this.selectedDate = date;
    },
    formatDob(dob) {
      if (!dob) return null;
      const parts = String(dob).substring(0, 10).split('-');
      if (parts.length !== 3) return null;
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    },
    openPatient(item) {
      this.selectedPatient = {
        entity_id: item.entityId,
        patient_identifier: item.patientIdentifier,
        patient_name: item.patientName,
      };
    },
    closeDrawer() {
      this.selectedPatient = null;
    },
    startDrag(e) {
      e.preventDefault();
      const container = e.target.parentElement;
      const onMove = (ev) => {
        const rect = container.getBoundingClientRect();
        let pct = ((ev.clientX - rect.left) / rect.width) * 100;
        pct = Math.max(25, Math.min(75, pct));
        this.splitPct = Math.round(pct);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        localStorage.setItem('cm_cal_split_pct', String(this.splitPct));
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    dayCellClasses(day) {
      return {
        'ring-2 ring-inset ring-blue-400': day.isSelected,
        'ring-2 ring-inset ring-[#F76734]': day.isToday && !day.isSelected,
        'opacity-40': !day.isCurrentMonth,
      };
    },
    dayCellStyle(day) {
      if (!day.isCurrentMonth || day.taskCount === 0) {
        return { backgroundColor: '#FFFFFF' };
      }
      const intensity = this.maxTaskCount > 0
        ? Math.min(day.taskCount / this.maxTaskCount, 1)
        : 0;
      const r = 255;
      const g = Math.round(255 - intensity * (255 - 221));
      const b = Math.round(255 - intensity * (255 - 210));
      return { backgroundColor: `rgb(${r}, ${g}, ${b})` };
    },
    dayNumberClasses(day) {
      return {
        'text-gray-900': day.isCurrentMonth && !day.isToday,
        'text-[#F76734] font-bold': day.isToday,
        'text-gray-400': !day.isCurrentMonth,
      };
    },
  },
};
</script>
